import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaTrashAlt } from "react-icons/fa";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input, FormField, Checkbox } from "./Form";
import { SegmentedControl } from "./Toolbar";
import { useToast } from "./Toast";
import { redirectsApi } from "../../services/toolsApi";

/**
 * AdjustSourceModal
 *
 * Admin tool to surgically delete a single source's (campaignType's) scans by
 * date, plus a shortcut to delete the entire source. Mirrors the legacy
 * dashboard's per-source adjustment, minus the raw integer "decrease by N"
 * (that mode updates the campaign doc only and would desync the new
 * daily-stats-driven charts — so the UI exposes date deletion only, which
 * reconciles both the campaign doc and qr_daily_stats).
 *
 * Date deletion → redirectsApi.decreaseCount → /api/qrCountDecrease.
 * "Delete entire source" delegates to the parent's DangerousActionDialog via
 * onDeleteEntireSource (a type-to-confirm destructive action).
 */
const MODE_OPTIONS = [
  { value: "range", label: "Between dates" },
  { value: "after", label: "After a date" },
  { value: "before", label: "Before a date" },
];

// Local YYYY-MM-DD → ISO. Start = midnight, end = end-of-day so a range is
// inclusive of both calendar days.
const startOfDayIso = (d) => new Date(`${d}T00:00:00`).toISOString();
const endOfDayIso = (d) => new Date(`${d}T23:59:59.999`).toISOString();

const AdjustSourceModal = ({
  isOpen,
  onClose,
  campaignId,
  campaignType,
  displayName,
  currentCount = 0,
  onDeleteEntireSource,
  onSuccess,
}) => {
  const toast = useToast();
  const [mode, setMode] = useState("range");
  const [afterDate, setAfterDate] = useState("");
  const [beforeDate, setBeforeDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode("range");
      setAfterDate("");
      setBeforeDate("");
      setFromDate("");
      setToDate("");
      setUnderstood(false);
    }
  }, [isOpen]);

  // Build the qrCountDecrease opts + a human description for the current mode.
  const buildOps = () => {
    if (mode === "after") {
      if (!afterDate) return null;
      return {
        opts: { deleteAfterDate: startOfDayIso(afterDate) },
        desc: `all "${campaignType}" redirects on or after ${afterDate}`,
      };
    }
    if (mode === "before") {
      if (!beforeDate) return null;
      return {
        opts: { deleteBeforeDate: startOfDayIso(beforeDate) },
        desc: `all "${campaignType}" redirects before ${beforeDate}`,
      };
    }
    // range
    if (!fromDate || !toDate || fromDate > toDate) return null;
    return {
      opts: {
        deleteFromDate: startOfDayIso(fromDate),
        deleteToDate: endOfDayIso(toDate),
      },
      desc: `all "${campaignType}" redirects between ${fromDate} and ${toDate}`,
    };
  };

  const built = buildOps();
  const canDelete = !!built && understood && !isLoading;

  const handleDelete = async () => {
    if (!built || !understood) return;
    setIsLoading(true);
    try {
      const result = await redirectsApi.decreaseCount(
        campaignId,
        campaignType,
        built.opts
      );
      const n = result?.deletedScansCount;
      toast.success(
        typeof n === "number"
          ? `Deleted ${n} "${campaignType}" redirect${n === 1 ? "" : "s"}`
          : result?.message || "Redirects deleted"
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error deleting redirects by date:", error);
      toast.error(error.message || "Failed to delete redirects", {
        debugInfo: {
          operation: "decreaseCount",
          params: { campaignId, campaignType, ...built.opts },
          error: error.stack || error.toString(),
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manage source — ${campaignType}`}
      size="small"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>
          {displayName} ·{" "}
          <b style={{ color: "#374151" }}>{currentCount.toLocaleString()}</b>{" "}
          redirects in current view
        </div>

        <FormField
          label="Delete redirects by date"
          helpText="Removes the matching redirect records and reconciles the daily totals — this cannot be undone."
        >
          <SegmentedControl
            value={mode}
            onChange={setMode}
            options={MODE_OPTIONS}
            variant="joined"
          />
        </FormField>

        {mode === "after" && (
          <FormField label="On or after">
            <Input
              type="date"
              value={afterDate}
              onChange={(e) => setAfterDate(e.target.value)}
              disabled={isLoading}
            />
          </FormField>
        )}

        {mode === "before" && (
          <FormField label="Before">
            <Input
              type="date"
              value={beforeDate}
              onChange={(e) => setBeforeDate(e.target.value)}
              disabled={isLoading}
            />
          </FormField>
        )}

        {mode === "range" && (
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <FormField label="From">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={isLoading}
                  fullWidth
                />
              </FormField>
            </div>
            <div style={{ flex: 1 }}>
              <FormField label="To">
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={isLoading}
                  fullWidth
                />
              </FormField>
            </div>
          </div>
        )}

        {mode === "range" && fromDate && toDate && fromDate > toDate && (
          <div style={{ fontSize: "0.75rem", color: "#dc2626" }}>
            "From" must be on or before "To".
          </div>
        )}

        {built && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: "0.75rem",
              color: "#991b1b",
            }}
          >
            This will permanently delete {built.desc}.
          </div>
        )}

        <Checkbox
          label="I understand this permanently deletes redirect records"
          checked={understood}
          onChange={(e) => setUnderstood(e.target.checked)}
          disabled={isLoading}
        />

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            borderTop: "1px solid #f1f5f9",
            paddingTop: 12,
          }}
        >
          <Button
            variant="ghost"
            size="small"
            icon={<FaTrashAlt />}
            onClick={() => {
              onClose();
              if (onDeleteEntireSource)
                onDeleteEntireSource(campaignId, campaignType);
            }}
            disabled={isLoading}
            title="Delete every redirect for this source"
          >
            Delete entire source
          </Button>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={!canDelete}>
              {isLoading ? "Deleting…" : "Delete redirects"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

AdjustSourceModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  campaignId: PropTypes.string,
  campaignType: PropTypes.string,
  displayName: PropTypes.string,
  currentCount: PropTypes.number,
  onDeleteEntireSource: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default AdjustSourceModal;
